// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

/**
 * Sprint 17.9.0 — Main tour orchestrator.
 * Wraps driver.js + action engine with floating TourController.
 */

import { useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { TourScript } from '@vn-grc/shared';
import {
  useTourEngine,
  resolveTourScriptTitle,
  resolveTourStepTitle,
  type TourMode,
} from '../hooks/useTourEngine.js';
import { useLocale } from '../hooks/useLocale.js';
import { TourController } from './TourController.js';
import { isVisibleTourElement } from '../hooks/tour-engine-dom.js';
import './tour-runner.css';

/**
 * Sprint 63.44.0 (D5, WCAG 2.4.3) — return focus where the tour was started,
 * or, when that is not a usable place, to the page's main heading, then its
 * main landmark. The gallery's Start button is disabled while the tour
 * launches, so the recorded origin is often <body>; an origin that has since
 * been removed or disabled (a navigating tour, a busy button) is no better.
 * Each candidate counts only if it actually receives focus.
 */
function restoreTourOriginFocus(origin: HTMLElement | null): void {
  const usable =
    origin &&
    origin !== document.body &&
    origin !== document.documentElement &&
    origin.isConnected &&
    !origin.matches(':disabled');
  if (usable) {
    origin.focus({ preventScroll: true });
    if (document.activeElement === origin) return;
  }
  for (const selector of ['main h1', 'main, [role="main"]', 'h1']) {
    const target = document.querySelector<HTMLElement>(selector);
    if (!target) continue;
    // Gate E12.5 pass 1 (P3-5) — focusable only while it holds focus: a
    // tabindex added here is removed on blur (one the page set is kept).
    const added = !target.hasAttribute('tabindex');
    if (added) target.tabIndex = -1;
    target.focus({ preventScroll: true });
    if (document.activeElement === target) {
      if (added) {
        // Gate E12.5 pass 2 (P3-5) — a window or tab switch also fires `blur`
        // while the heading stays the active element; only a real blur ends it.
        const onBlur = () => {
          if (document.activeElement === target) return;
          target.removeAttribute('tabindex');
          target.removeEventListener('blur', onBlur);
        };
        target.addEventListener('blur', onBlur);
      }
      return;
    }
    if (added) target.removeAttribute('tabindex');
  }
}

interface TourRunnerProps {
  script: TourScript | null;
  mode?: TourMode;
  startStepIndex?: number;
  onComplete?: (module: string, revision?: string) => void;
  onExit?: () => void;
  /** Called with the module and the index of the step reached, each time the tour moves to a different step. */
  onStepChange?: (module: string, stepIndex: number, revision?: string) => void;
}

export function TourRunner({
  script,
  mode,
  startStepIndex = 0,
  onComplete,
  onExit,
  onStepChange,
}: TourRunnerProps) {
  const [state, actions] = useTourEngine();
  const { locale, t } = useLocale();
  const { status, currentStepIndex, mode: engineMode, speed, script: activeScript } = state;
  const exitedRef = useRef(false);
  const originRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    originRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    return () => restoreTourOriginFocus(originRef.current);
  }, []);

  // Fix-sprint 20260419-1455 F6 — handleExit moved above effects that
  // reference it so it can be a declared dep without TDZ issues.
  const handleExit = useCallback(() => {
    exitedRef.current = true;
    actions.stop();
    onExit?.();
  }, [actions, onExit]);

  // Start tour when script changes
  useEffect(() => {
    if (script && status === 'idle' && !exitedRef.current) {
      actions.start(script, mode, startStepIndex);
    }
    // Fix-sprint 20260419-1455 F6 — full dep list.
  }, [script, status, actions, mode, startStepIndex]);

  // Handle tour finished
  useEffect(() => {
    if (status === 'finished' && activeScript) {
      if (activeScript.access_filtered) onExit?.();
      else onComplete?.(activeScript.module, activeScript.revision);
    }
    // Fix-sprint 20260419-1455 F6 — include activeScript + onComplete.
  }, [status, activeScript, onComplete, onExit]);

  // Report the step reached so the tour resumes where the person left it.
  // The first report is the starting step itself, which is already stored.
  const reportedStepRef = useRef<number | null>(null);
  useEffect(() => {
    if (!activeScript || (status !== 'running' && status !== 'paused')) return;
    if (reportedStepRef.current === null) {
      reportedStepRef.current = currentStepIndex;
      return;
    }
    if (reportedStepRef.current === currentStepIndex) return;
    reportedStepRef.current = currentStepIndex;
    onStepChange?.(activeScript.module, currentStepIndex, activeScript.revision);
  }, [activeScript, status, currentStepIndex, onStepChange]);

  // Keyboard shortcuts
  useEffect(() => {
    if (status === 'idle') return undefined;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Tab') {
        // driver.js traps Tab inside its popover and target, excluding our
        // portaled toolbar. Own the complete focus scope before its bubble listener.
        const scope = document.querySelectorAll<HTMLElement>(
          '[data-tour-controls] button, .driver-active-element:is(button,input,select,textarea,a[href],[tabindex]), .driver-active-element button, .driver-active-element input, .driver-active-element select, .driver-active-element textarea, .driver-active-element a[href], .driver-popover button, .grc-tour-popover[tabindex="0"]',
        );
        const available = Array.from(scope).filter(
          (el) => !el.matches(':disabled') && el.tabIndex >= 0 && isVisibleTourElement(el),
        );
        if (available.length) {
          e.preventDefault();
          e.stopImmediatePropagation();
          const index = available.indexOf(document.activeElement as HTMLElement);
          available[(index + (e.shiftKey ? -1 : 1) + available.length) % available.length].focus();
        }
        return;
      }
      // Keep native scrolling (including Space) in an overflowing narration.
      // Escape still exits; Tab above still reaches every controller action.
      if (
        e.key !== 'Escape' &&
        e.target instanceof HTMLElement &&
        e.target.closest('.grc-tour-popover[tabindex="0"]')
      )
        return;
      if (
        e.key !== 'Escape' &&
        e.target instanceof HTMLElement &&
        e.target.closest('button, input, textarea, select, [contenteditable="true"]')
      )
        return;
      switch (e.key) {
        case ' ':
          e.preventDefault();
          if (status === 'paused') actions.resume();
          else if (status === 'running') actions.pause();
          break;
        case 'Escape':
          handleExit();
          break;
        case 'ArrowRight':
          actions.nextStep();
          break;
        case 'ArrowLeft':
          actions.prevStep();
          break;
      }
    }

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
    // Fix-sprint 20260419-1455 F6 — actions is stable from useTourEngine
    // but list it explicitly; handleExit is memoized above.
  }, [status, actions, handleExit]);

  const handlePlayPause = useCallback(() => {
    if (status === 'paused') actions.resume();
    else if (status === 'running') actions.pause();
  }, [status, actions]);

  // Sprint 63.44.0 (D6) — the position AND the step's title, in the reader's
  // language: the popover (role="dialog") holding the title never takes focus,
  // so the position alone told a screen-reader user nothing about the step.
  // The narration stays in the popover (labelled and described) to keep this
  // concise. The template is the one the quick starts announce a step with.
  const stepAnnouncement = useMemo(() => {
    if (!activeScript || status === 'idle' || status === 'finished') return '';
    const step = activeScript.steps[currentStepIndex];
    return t.admin.onboarding_maturity.quick_starts_page.step_changed
      .replace('{current}', String(currentStepIndex + 1))
      .replace('{total}', String(activeScript.steps.length))
      .replace('{title}', step ? resolveTourStepTitle(step, locale) : '');
  }, [activeScript, status, currentStepIndex, t, locale]);

  if (!activeScript || status === 'idle' || status === 'finished') return null;
  if (status === 'failed')
    return createPortal(
      <div className="grc-tour-controller" role="alert" data-tour-controls>
        <div className="grc-tour-controller__body">
          <span>
            {t.common.operation_failed}:{' '}
            {resolveTourStepTitle(activeScript.steps[currentStepIndex], locale)}
          </span>
          <button className="grc-tour-controller__btn" onClick={handleExit} autoFocus>
            {t.onboarding.tours.exit}
          </button>
        </div>
      </div>,
      document.body,
    );

  return (
    <>
      <div
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {stepAnnouncement}
      </div>
      <TourController
        status={status}
        currentStep={currentStepIndex}
        totalSteps={activeScript.steps.length}
        mode={engineMode}
        speed={speed}
        tourTitle={resolveTourScriptTitle(activeScript, locale)}
        onPlayPause={handlePlayPause}
        onNext={actions.nextStep}
        onPrev={actions.prevStep}
        onSpeedChange={actions.setSpeed}
        onModeChange={actions.setMode}
        onExit={handleExit}
      />
    </>
  );
}
